import React from "react";
import { t } from "ttag";
import { Spinner, Table } from "@coolremote/shared";
import {
  useCreateModbusDeviceTemplate,
  useDeleteModbusDeviceTemplate,
  useGetSiteModbusDeviceTemplates,
  useRenameModbusDeviceTemplate,
} from "../../../../hooks/useModbusDeviceTemplatesApi";
import { IModbusDeviceTemplate } from "../../types";
import LibraryActionBanner from "./LibraryActionBanner";
import LibraryToolbar from "./LibraryToolbar";
import { buildCheckboxColumn, buildDeleteActionColumn, buildNameColumn } from "./libraryColumns";
import { ILibrarySaveApi, useLibraryTab } from "./useLibraryTab";
import styles from "./ModbusSiteLibraryManagementDialog.module.css";

interface IProps {
  siteId?: string;
  active: boolean;
  /** Lets the dialog drive this tab's Save from its own footer button. */
  onSaveApiChange?: (api: ILibrarySaveApi | null) => void;
}

const DevicesLibraryTab: React.FC<IProps> = ({ siteId, active, onSaveApiChange }) => {
  const { mutate: createDeviceTemplate } = useCreateModbusDeviceTemplate({});
  const { mutate: renameDeviceTemplate } = useRenameModbusDeviceTemplate({});
  const { mutate: deleteDeviceTemplate } = useDeleteModbusDeviceTemplate({});
  const { data, isLoading, refetch } = useGetSiteModbusDeviceTemplates(siteId, active) as unknown as {
    data: IModbusDeviceTemplate[] | null;
    isLoading: boolean;
    refetch: () => void;
  };

  const lib = useLibraryTab<IModbusDeviceTemplate>({
    items: data ?? [],
    refetch,
    active,
    siteId,
    create: createDeviceTemplate,
    rename: (templateId, name) => renameDeviceTemplate({ templateId, name }),
    remove: deleteDeviceTemplate,
    pickExportFields: ({ name, vendorName, modelName, description, timeoutMs, units }) => ({
      name, vendorName, modelName, description, timeoutMs, units,
    }),
    importedItemsLabel: t`devices`,
    onSaveApiChange,
  });

  return (
    <div className={styles.tabRoot}>
      <LibraryToolbar
        search={lib.search}
        onSearchChange={lib.setSearch}
        exportMode={lib.exportMode}
        selectedCount={lib.selectedIds.size}
        onEnterExportMode={() => lib.setExportMode(true)}
        onExitExportMode={lib.exitExportMode}
        onExportSelected={lib.handleExportSelected}
        siteId={siteId}
        importing={lib.importing}
        importProgress={lib.importProgress}
        fileInputRef={lib.fileInputRef}
        onFileChange={lib.handleFileChange}
      />
      <LibraryActionBanner banner={lib.banner} />

      <div className={styles.tabBody}>
      {/* Spinner only on the first load — a background refetch (e.g. after a
          rename) keeps the table on screen instead of blanking the tab. */}
      {isLoading && !data ? (
        <div className={styles.loaderWrap}>
          <Spinner size={28} />
        </div>
      ) : (
        <Table
          stickyHeader
          data={lib.filtered}
          getRowKey={(row: IModbusDeviceTemplate, index: number) => row.id ?? index}
          emptyMessage={lib.search ? t`No templates match your search.` : t`No device templates found for this site.`}
          columns={[
            ...buildCheckboxColumn(lib.exportMode, lib.allSelected, lib.toggleAll, lib.selectedIds, lib.toggleSelected),
            ...buildNameColumn<IModbusDeviceTemplate>(t`DEVICE NAME`, "28%", lib.editingNames, lib.setDraftName, lib.pendingDeleteIds),
            {
              id: "vendorName",
              title: t`MANUFACTURER`,
              width: "11%",
              render: (row: IModbusDeviceTemplate) => row.vendorName || "–",
            },
            {
              id: "modelName",
              title: t`MODEL`,
              width: "11%",
              render: (row: IModbusDeviceTemplate) => row.modelName || "–",
            },
            {
              id: "units",
              title: t`UNIT COUNT`,
              width: "8%",
              align: "center",
              render: (row: IModbusDeviceTemplate) => (row.units?.length ?? 0),
            },
            {
              id: "registers",
              title: t`REGISTER COUNT`,
              width: "9%",
              align: "center",
              render: (row: IModbusDeviceTemplate) => (row.units ?? []).reduce((sum, u) => sum + (u.rows?.length ?? 0), 0),
            },
            {
              id: "unitNames",
              title: t`FUNCTIONAL UNITS NAMES`,
              width: lib.exportMode ? "28%" : "23%",
              render: (row: IModbusDeviceTemplate) => (
                <div className={styles.paramList}>{(row.units ?? []).map((u) => u.name).join(", ")}</div>
              ),
            },
            ...buildDeleteActionColumn(lib.exportMode, lib.pendingDeleteIds, lib.togglePendingDelete),
          ]}
        />
      )}
      </div>
    </div>
  );
};

export default DevicesLibraryTab;
